import { useState, useEffect } from "react";

export default function ScriptStep({
  script,
  onScriptChange,
  onSaveScript,
  onRegenerate,
  onBack,
  onNext,
  isRegenerating,
  isSaving = false,
  wordCount,
  scriptId,
}) {
  const [copied, setCopied] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedScript, setLastSavedScript] = useState(script);
  const [saveStatus, setSaveStatus] = useState("saved"); // "saved", "saving", "unsaved"

  const estimatedDuration = Math.ceil((wordCount / 150) * 60);
  const durationMinutes = Math.floor(estimatedDuration / 60);
  const durationSeconds = estimatedDuration % 60;

  // Track changes
  useEffect(() => {
    if (script !== lastSavedScript) {
      setHasUnsavedChanges(true);
      setSaveStatus("unsaved");
    }
  }, [script, lastSavedScript]);

  // Update save status when saving
  useEffect(() => {
    if (isSaving) {
      setSaveStatus("saving");
    }
  }, [isSaving]);

  const handleSave = async () => {
    if (!hasUnsavedChanges || !onSaveScript) return;
    
    setSaveStatus("saving");
    try {
      await onSaveScript(script);
      setLastSavedScript(script);
      setHasUnsavedChanges(false);
      setSaveStatus("saved");
    } catch (err) {
      console.error("Failed to save script:", err);
      setSaveStatus("unsaved");
    }
  };

  const handleScriptChange = (value) => {
    onScriptChange(value);
    setHasUnsavedChanges(true);
    setSaveStatus("unsaved");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(script);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32 sm:pb-28">

        {/* Back button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Topic
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 mb-1">
              Review your script
            </h1>
            <p className="text-sm text-gray-500">
              AI drafted this based on your topic. Feel free to polish it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRegenerate}
              disabled={isRegenerating}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                isRegenerating
                  ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              <svg className={`w-4 h-4 ${isRegenerating ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {isRegenerating ? "Regenerating..." : "Regenerate"}
            </button>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Script Editor */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
          <textarea
            value={script}
            onChange={(e) => handleScriptChange(e.target.value)}
            className="w-full px-5 py-5 text-base text-gray-800 leading-relaxed resize-none focus:outline-none min-h-[400px] bg-transparent"
            placeholder="Your script will appear here..."
          />

          {/* Footer stats bar */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <strong className="text-gray-800">{wordCount}</strong> words
              </span>
              <span className="w-px h-4 bg-gray-200" />
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Est. <strong className="text-gray-800">{durationMinutes > 0 ? `${durationMinutes}m ` : ""}{durationSeconds}s</strong>
              </span>
            </div>
            <div className="flex items-center gap-3">
              {hasUnsavedChanges && onSaveScript && (
                <button
                  onClick={handleSave}
                  disabled={saveStatus === "saving"}
                  className="px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  {saveStatus === "saving" ? (
                    <>
                      <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}
              <div className={`flex items-center gap-1.5 text-xs font-medium ${
                saveStatus === "saved" ? "text-green-600" : 
                saveStatus === "saving" ? "text-yellow-600" : 
                "text-orange-500"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  saveStatus === "saved" ? "bg-green-500" : 
                  saveStatus === "saving" ? "bg-yellow-500" : 
                  "bg-orange-500"
                }`} />
                {saveStatus === "saved" && "Saved"}
                {saveStatus === "saving" && "Saving..."}
                {saveStatus === "unsaved" && "Unsaved"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Script ready for video generation
          </div>
          <button
            onClick={onNext}
            disabled={!script.trim()}
            className="px-8 py-3 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 shadow-sm"
          >
            Generate Video Assets
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
